import { useMemo } from "react";
import { Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { QuoteValues } from "@covertree/new-business-graphql";

import { PersonalizedQuotesListCardDetailsElement } from "./PersonalizedQuotesListCardDetailsElement";

interface Props {
  quote: QuoteValues;
  isTenantFlow: boolean;
  hasPremisesLiability: boolean;
  homeCoverageDescription: string;
  yourBelongingsDescription: string;
}

const settlementOptionToCodeMap: Record<string, string> = {
  "Actual Cash Value": "ACV",
  "Replacement Cost": "RCV",
  "Extended Replacement Cost": "ERC",
  "Full Repair Cost": "FRC",
};

export function PersonalizedQuotesListCardDetails({
  quote,
  isTenantFlow,
  hasPremisesLiability,
  homeCoverageDescription,
  yourBelongingsDescription,
}: Props) {
  const theme = useTheme();

  const { homeCoverageCode, yourBelongingsCode } = useMemo(
    () => ({
      homeCoverageCode:
        settlementOptionToCodeMap[homeCoverageDescription] || undefined,
      yourBelongingsCode:
        settlementOptionToCodeMap[yourBelongingsDescription] || undefined,
    }),
    [homeCoverageDescription, yourBelongingsDescription]
  );

  if (isTenantFlow) {
    return (
      <Grid
        container
        spacing={1}
        sx={{
          paddingTop: theme.spacing(3),
        }}>
        <Grid item xs={12} sm={4}>
          <PersonalizedQuotesListCardDetailsElement
            value={quote.yourBelongings}
            name={
              yourBelongingsCode
                ? `Your Belongings (${yourBelongingsCode})`
                : "Your Belongings"
            }
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <PersonalizedQuotesListCardDetailsElement
            value={quote.personalLiability}
            name="Personal Liability"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <PersonalizedQuotesListCardDetailsElement
            value={quote.lossOfUse}
            name="Loss of use"
            displayPercent
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={1}
      sx={{
        paddingTop: theme.spacing(3),
      }}>
      <Grid item xs={12} sm={4}>
        <PersonalizedQuotesListCardDetailsElement
          value={quote.homeCoverage}
          name={
            homeCoverageCode
              ? `Home Coverage (${homeCoverageCode})`
              : "Home Coverage"
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <PersonalizedQuotesListCardDetailsElement
          value={quote.yourBelongings}
          name={
            yourBelongingsCode
              ? `Your Belongings (${yourBelongingsCode})`
              : "Your Belongings"
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <PersonalizedQuotesListCardDetailsElement
          value={
            hasPremisesLiability
              ? quote.premisesLiability
              : quote.personalLiability
          }
          name={`${hasPremisesLiability ? "Premises" : "Personal"} Liability`}
        />
      </Grid>
    </Grid>
  );
}
